import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationSkipped, Router, Event, RouterEvent } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Order, OrderManagerService, OrderRepositoryService, OrderHistoryComponent, eOrderType, eOrderTypeGroup, OrderMessagesComponent, UIConfigurationService, OrderUIConfiguration, eOrderStatusType, ConfigurationState, SearchResultsRepositoryService, SelectOption, BundlesRepositoryService, EnvelopeRepositoryService, Envelope, eEnvelopeType, UboTreeNodesService, UboTreeNode, ePartyType } from 'reg-hub-common';
import { filter, map, Subject, Subscription, takeUntil } from 'rxjs';
import { OrderValidationLogsComponent } from '../order-validation-logs/order-validation-logs.component';
import { PrintDataService } from '../../services/print-data/print-data.service';
import { OrdersHubService } from '../../services/hubs/orders-hub/orders-hub.service';
import { AddToBundleComponent } from '../add-to-bundle/add-to-bundle.component';

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class OrderComponent implements OnInit, OnDestroy {
  protected uiConfiguration!: OrderUIConfiguration;
  private unsubscribe$ = new Subject<void>();
  private eventSub$!: Subscription;
  private bundleOptions: SelectOption[] = [];
  protected uboTreeNode?: UboTreeNode;
  protected hubReportID?: string;
  protected linkableUboTreeNodes?: UboTreeNode[];
  protected downstreamShareholderHasUbo: boolean = true; // defaulting to true to reduce screen flicker

  orderId: string = "";
  order: Order = {
    referenceNumber: "",
    country: "",
    jurisdiction: "",
    orderTypeID: eOrderType.notSet
  } as Order;

  isLoadingOrder: boolean = false;
  showOrderFeeSummary: boolean = false;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected orderRepo: OrderRepositoryService,
    protected dialog: MatDialog,
    protected orderManager: OrderManagerService,
    protected uiConfigurationService: UIConfigurationService,
    protected ordersHub: OrdersHubService,
    protected printDataService: PrintDataService,
    protected configurationState: ConfigurationState,
    protected searchResultsService: SearchResultsRepositoryService,
    private bundlesRepo: BundlesRepositoryService,
    private uboTreeService: UboTreeNodesService) { }

  ngOnInit(): void {
    this.ordersHub.connect().subscribe();

    this.orderManager.currentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(order => {
        this.order = order;
      });

    this.activatedRoute.paramMap.subscribe(params => {
      this.orderId = params.get('orderID')!;
      this.getOrder(this.orderId);
    });

    this.shouldShowOrderSummary();

    this.eventSub$ = this.router.events
      .pipe(
        filter(event => event instanceof NavigationSkipped),
        map(event => event as NavigationSkipped))
      .subscribe((event: NavigationSkipped) => {
        if (event.url.includes(this.orderId)) {
          this.getOrder(this.orderId);
        }
      });

    // Subscribe to completed order events
    this.ordersHub.completedOrder$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(completedOrder => {
        if (completedOrder.id === this.orderId) {
          // this.order = completedOrder as Order;
          // this.order.orderStatusTypeID = "Complete";
          // this.orderManager.updateOrder(this.order);
          this.getOrder(this.orderId);
        }
      });

    // Subscribe to in progress order events
    this.ordersHub.inProgressOrder$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(inProgressOrder => {
        if (inProgressOrder.id === this.orderId) {
          // this.order = completedOrder as Order;
          // this.order.orderStatusTypeID = "Complete";
          // this.orderManager.updateOrder(this.order);
          this.getOrder(this.orderId);
        }
      });
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.eventSub$.unsubscribe();
  }

  getUIConfiguration(order: Order) {
    //We use admin flag here to show all details for re-registration
    var useAdminFlag = order.orderTypeID === eOrderType.reregistration;

    this.uiConfigurationService.getOrderUIConfiguration(order, useAdminFlag).subscribe(config => {
      this.uiConfiguration = config;
      this.isLoadingOrder = false;
    });
  }

  openOrderHistory() {
    this.dialog.open(OrderHistoryComponent, {
      data: this.order,
      minWidth: '800px'
    });
  }

  openValidationLogs() {
    this.dialog.open(OrderValidationLogsComponent, {
      data: this.order,
      minWidth: '800px'
    });
  }

  openOrderMessages() {
    this.dialog.open(OrderMessagesComponent, {
      data: { order: this.order, customer: true },
      minWidth: '800px'
    });
  }

  messageCount() {
    let length = this.order?.orderMessages?.filter((u) => u.isCustomerVisible && !u.isReadByCustomer).length ?? 0;
    return length > 0 ? length.toString() : "";
  }

  private getOrder = (orderID: string) => {
    // this will call api/v1/orders/id
    this.isLoadingOrder = true;
    this.orderRepo.getOrder('orders', orderID)
      .subscribe({
        next: (order: any) => {
          if (order.orderStatusTypeID.toLowerCase() == "draft" ||
            order.orderStatusTypeID.toLowerCase() == "invalid" ||
            order.orderStatusTypeID.toLowerCase() == "historicaldraft") {
            this.router.navigate([
              ...this.getDraftRedirectUrl(order.orderTypeID),
              order.id
            ]);
          }

          this.order = order as Order;
          this.orderManager.updateOrder(this.order);
          this.getUIConfiguration(this.order);
          this.getBundles();

          // probably open this up to all order types eventually, but for now wanting to save a call
          if (this.order.orderTypeID === eOrderType.businessReport ||
            this.order.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport ||
            this.order.orderTypeID === eOrderType.pepsAndSanctions) {
            this.uboTreeService.getUboTrees({ orderID: this.order.id }, false, true).subscribe(nodes => {
              this.uboTreeNode = nodes.at(0);
              this.hubReportID = this.uboTreeNode?.hubReportID;

              if (!this.hubReportID) {
                this.uboTreeService.getUboTrees({
                  businessNumber: this.order.businessReportCriteria?.number ?? '',
                  businessCountry: order.country,
                  businessJurisdiction: order.jurisdiction,
                  partyTypes: [ePartyType.businessShareholder],
                  includesNoOrderOfTypes: [eOrderType.businessReport]
                }, true).subscribe(nodes => this.linkableUboTreeNodes = nodes);
              }

              if (this.order.orderTypeID === eOrderType.businessReport) {
                this.setDownstreamShareholderHasUbo();
              }
            });
          }
        },
        error: (error: any) => {
          // Handle error
          console.error('Error:', error);
        }
      });
  };

  private getDraftRedirectUrl(orderTypeID: eOrderType | string): string[] {
    const parts = ['orders'];

    switch (orderTypeID) {
      case eOrderType.amendment:
      case eOrderType.assignment:
      case eOrderType.discharge:
      case eOrderType.lien:
      case eOrderType.renewal:
      case eOrderType.basicDischarge:
      case eOrderType.basicRenewal:
      case eOrderType.repairLien:
      case eOrderType.notSet:
      case eOrderType.reregistration:
        parts.push('ppsa-registration');
        break;
      case eOrderType.assetSearch:
      case eOrderType.assetMultiSearch:
      case eOrderType.debtorSearch:
      case eOrderType.debtorMultiSearch:
      case eOrderType.certifiedDebtorSearch:
      case eOrderType.registrationNumberSearch:
        parts.push('ppsa-search');
        break;
      case eOrderType.businessSearch:
      case eOrderType.businessReport:
      case eOrderType.ultimateBeneficialOwnershipReport:
      case eOrderType.pepsAndSanctions:
        parts.push('business');
        break;
      case eOrderType.landTitleSearch:
        parts.push('land');
        break;
      case eOrderType.knowYourCustomer:
        parts.push('kyc');
        break;
    }

    return parts;
  }

  isActionable(): boolean {
    return (this.uiConfiguration?.hasActions ?? false) ||
      !!this.uiConfiguration.actionUIConfiguration;
  }

  print() {
    this.printDataService.printOrder();
  }

  showPrintButton() {
    return this.printDataService.shouldShowPrintOrderButton(this.order);
  }

  showHubReportButton() {
    return this.order.orderStatusTypeID !== eOrderStatusType.cancelled;
  }

  shouldShowOrderSummary() {
    this.configurationState.hasOrderFeeVisibility().then(show => {
      this.showOrderFeeSummary = show;
    });
  }

  shouldShowBundlesGrid(): boolean {
    return ((this.order.orderGroup?.bundles?.length ?? 0) > 0);
  }

  getBundles() {
    if (this.order.orderGroup) {
      this.bundlesRepo.getBundlesAsSelectOptions(this.order.orderGroup!.corporationID).subscribe(bundles => {
        this.bundleOptions = bundles;
      });
    }
  }

  getAvailableBundles(): SelectOption[] {
    var orderBundles = this.order.orderGroup?.bundles?.map(bundle => bundle.id) ?? [];

    return this.bundleOptions.filter(opt => !orderBundles.includes(opt.value as string));
  }

  showBundlesButton() {
    let availableBundles = this.getAvailableBundles();

    return availableBundles.length > 0;
  }

  addToBundle() {
    const dialogRef = this.dialog.open(AddToBundleComponent, {
      data: { order: this.order, bundles: this.getAvailableBundles() },
      minWidth: '500px'
    });
  }

  onHubReportClick() {
    this.router.navigate(['hubreportsv2', this.hubReportID]); //UBO2TODO: correct path
  }

  protected setDownstreamShareholderHasUbo() {
    if (this.uboTreeNode) {
      this.downstreamShareholderHasUbo = this.downstreamShareholderChildHasUbo(this.uboTreeNode);
    } else {
      this.downstreamShareholderHasUbo = false;
    }
  }

  protected downstreamShareholderChildHasUbo(node: UboTreeNode) {
    for (let child of node.children.filter(c => c.party?.partyTypeID === ePartyType.businessShareholder)) {
      if (child.orders.some(o => o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport &&
        o.orderStatusTypeID !== eOrderStatusType.cancelled) ||
        this.downstreamShareholderChildHasUbo(child)) {
        return true;
      }
    }

    return false;
  }
}
